import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listWorkspaceMembers,
  inviteWorkspaceMember,
  updateWorkspaceMemberPermission,
  removeWorkspaceMember,
  cancelWorkspaceInvitation,
  resendInvitation,
  transferWorkspaceOwnership,
} from '../api'
import { showSimpleSuccess, showSimpleError } from '@/lib/toast'
import { useActiveWorkspace } from '@/state/selection.store'

const mapMembersResponse = (data = {}, workspaceId) => {
  const members = data.data?.members || []
  const pendingInvitations = data.data?.pendingInvitations || []
  const workspaceOwnerId = data.data?.owner || null

  const mappedActive = members.map(m => {
    const userId = m.user?._id || m.user?.id || m._id || m.id
    const access = m.access || 'member'
    const normalizedAccess = typeof access === 'string' ? access.toLowerCase() : access

    // Check if this member is the workspace owner by comparing user ID
    const isOwner = workspaceOwnerId && String(userId) === String(workspaceOwnerId)

    return {
      id: userId,
      name: m.user?.name || m.name,
      email: m.user?.email || m.email,
      avatar: m.user?.avatar || m.avatar || null,
      // If this is the owner, show 'owner' role instead of their access level
      role: isOwner ? 'owner' : normalizedAccess,
      status: 'active',
      joinedDate: m.createdAt || m.joinedAt || null,
      projects: m.projects || [],
      workspaceId,
      isOwner,
    }
  })

  const now = new Date()
  const mappedInvited = pendingInvitations.map(inv => {
    const expiresAt = inv.expiresAt ? new Date(inv.expiresAt) : null
    const isExpired = expiresAt && expiresAt < now

    return {
      id: inv._id || inv.id,
      name: '',
      email: inv.email,
      role: inv.permission || 'viewer',
      status: isExpired ? 'expired' : 'invited',
      joinedDate: inv.invitedAt || null,
      expiresAt: inv.expiresAt || null,
      invitationId: inv._id || inv.id,
      workspaceId,
      isOwner: false,
    }
  })

  return [...mappedActive, ...mappedInvited]
}

export function useWorkspaceMembers() {
  const { workspaceId } = useActiveWorkspace()
  const queryClient = useQueryClient()

  const isValidWorkspace = Boolean(workspaceId && typeof workspaceId === 'string' && /^[a-f\d]{24}$/i.test(workspaceId))

  const membersQuery = useQuery({
    queryKey: ['workspaceMembers', workspaceId],
    queryFn: () => listWorkspaceMembers(workspaceId),
    enabled: isValidWorkspace,
    retry: (failureCount, error) => {
      // Never retry 4xx errors (not found, forbidden, etc.)
      const status = error?.status || error?.response?.status
      if (status && status >= 400 && status < 500) return false
      return failureCount < 2
    },
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['workspaceMembers', workspaceId] })
  }

  const inviteMutation = useMutation({
    mutationFn: ({ email, permission }) => inviteWorkspaceMember({ workspaceId, email, permission }),
    onSuccess: () => {
      showSimpleSuccess('Invitation sent successfully')
      invalidate()
    },
    onError: (error) => {
      showSimpleError(error.message || 'Failed to send invitation')
    },
  })

  const updatePermissionMutation = useMutation({
    mutationFn: ({ userId, permission }) => {
      return updateWorkspaceMemberPermission({ workspaceId, userId, permission })
    },
    onMutate: async ({ userId, permission }) => {
      await queryClient.cancelQueries({ queryKey: ['workspaceMembers', workspaceId] })

      const previousMembers = queryClient.getQueryData(['workspaceMembers', workspaceId])

      queryClient.setQueryData(['workspaceMembers', workspaceId], (old) => {
        if (!old) return old
        const updatedData = { ...old }
        if (updatedData.data?.members) {
          updatedData.data.members = updatedData.data.members.map(m => {
            if ((m.user?._id || m._id) === userId) {
              return { ...m, access: permission.toLowerCase() }
            }
            return m
          })
        }
        return updatedData
      })

      return { previousMembers }
    },
    onSuccess: () => {
      showSimpleSuccess('Permission updated')
      invalidate()
    },
    onError: (error, variables, context) => {
      if (context?.previousMembers) {
        queryClient.setQueryData(['workspaceMembers', workspaceId], context.previousMembers)
      }
      showSimpleError(error.message || 'Failed to update permission')
    },
  })

  const removeMutation = useMutation({
    mutationFn: ({ userId }) => removeWorkspaceMember({ workspaceId, userId }),
    onSuccess: (data) => {
      showSimpleSuccess(data?.message || 'Member removed successfully')
      invalidate()
    },
    onError: (error) => {
      showSimpleError(error.message || 'Failed to remove member')
    },
  })

  const cancelInviteMutation = useMutation({
    mutationFn: ({ invitationId }) => cancelWorkspaceInvitation({ workspaceId, invitationId }),
    onSuccess: () => {
      showSimpleSuccess('Invitation cancelled')
      invalidate()
    },
    onError: (error) => {
      showSimpleError(error.message || 'Failed to cancel invitation')
    },
  })

  const resendInviteMutation = useMutation({
    mutationFn: (invitationId) => resendInvitation(invitationId),
    onSuccess: (data) => {
      showSimpleSuccess(data?.message || 'Invitation resent successfully')
      invalidate()
    },
    onError: (error) => {
      showSimpleError(error.message || 'Failed to resend invitation')
    },
  })

  const transferOwnershipMutation = useMutation({
    mutationFn: ({ newOwnerId }) => transferWorkspaceOwnership({ workspaceId, newOwnerId }),
    onSuccess: (data) => {
      showSimpleSuccess(data?.message || 'Ownership transferred successfully')
      invalidate()
    },
    onError: (error) => {
      showSimpleError(error.message || 'Failed to transfer ownership')
    },
  })

  const members = mapMembersResponse(membersQuery.data, workspaceId)

  return {
    workspaceId,
    members,
    loading: membersQuery.isLoading,
    error: membersQuery.error,
    inviteMember: inviteMutation.mutateAsync,
    updatePermission: updatePermissionMutation.mutateAsync,
    removeMember: removeMutation.mutateAsync,
    cancelInvitation: cancelInviteMutation.mutateAsync,
    resendInvitation: resendInviteMutation.mutateAsync,
    transferOwnership: transferOwnershipMutation.mutateAsync,
    refetch: membersQuery.refetch,
  }
}

